// PhoneIntegrationView — Settings panel for BYO ("bring your own") SMS + voice.
//
// FOUNDATION ONLY. Lets an agent connect THEIR OWN provider account
// (SignalWire — the recommended/first one; Twilio + RingCentral share the
// same shape). Credentials are sent to the backend and stored AES-256
// encrypted. Nothing is sent from here yet — the actual SMS/call send flow
// is a later phase that builds on these stored connections.
//
// HIPAA: SMS/voice via these providers CAN carry PHI (they sign BAAs).
// The auth token is write-only — the backend never returns it.
import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Phone, Plug, RefreshCw, Trash2, ShieldCheck, Info, ExternalLink, Check, MessageSquare, AlertTriangle, CheckCircle2, ChevronDown, Copy } from "lucide-react";
import {
  getPhoneIntegrations,
  connectPhoneProvider,
  disconnectPhoneProvider,
  configurePhoneInbound,
  disablePhoneInbound,
  setPhoneChannels,
  configureQuoWebhooks,
  setOwnVoiceBaa,
  startGotoAuthorization,
  setGotoLine,
  attestSmsA2p,
} from "../api.js";
import { costoAproximadoDeLaLinea } from "../avisoDeCostoSms.js";
import { confirmDialog } from "./ui.jsx";
import { toast } from "./Toast.jsx";

export function PhoneIntegrationView({ lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);

  // Provider catalog. SignalWire is the recommended/first one (the one Lissette
  // chose: signs a HIPAA BAA, cheaper, no platform fee). The others are the
  // same mold, added so agents who already use them can bring their own.
  const PROVIDERS = [
    {
      id: "SIGNALWIRE",
      name: "SignalWire",
      recommended: true,
      accountLabel: "Project ID",
      tokenLabel: "API Token",
      needsSpace: true,
      signupUrl: "https://signalwire.com",
      blurb: t(
        "Recomendado: el más barato para uso normal y sin cuota fija de plataforma. Firma el acuerdo HIPAA (BAA), aunque ese plan tiene un mínimo mensual.",
        "Recommended: cheapest for regular use, with no fixed platform fee. It signs the HIPAA agreement (BAA), though that plan has a monthly minimum.",
      ),
    },
    {
      id: "TWILIO",
      name: "Twilio",
      accountLabel: "Account SID",
      tokenLabel: "Auth Token",
      needsSpace: false,
      signupUrl: "https://www.twilio.com",
      blurb: t(
        "El más conocido. Para firmar el acuerdo HIPAA exige su edición de pago “Security”.",
        "The best-known one. Signing the HIPAA agreement requires their paid “Security” edition.",
      ),
    },
    {
      id: "RINGCENTRAL",
      name: "RingCentral",
      accountLabel: "Client ID",
      tokenLabel: "Client Secret",
      needsSpace: false,
      needsJwt: true,
      jwtLabel: "JWT (credencial server-to-server)",
      signupUrl: "https://developers.ringcentral.com",
      blurb: t(
        "Si ya usas RingCentral para tu teléfono de negocio, conéctalo aquí.",
        "If you already use RingCentral for your business phone, connect it here.",
      ),
    },
    {
      id: "MIGHTYCALL",
      name: "MightyCall",
      accountLabel: "Client ID",
      tokenLabel: "Client Secret",
      needsSpace: false,
      needsJwt: true,
      jwtLabel: "API Key (x-api-key)",
      jwtPlaceholder: "00000000-0000-0000-0000-000000000000",
      jwtHelp: t(
        "En MightyCall: Integraciones → API. Ahí obtienes el API Key, el Client ID y el Client Secret. Se guarda cifrada.",
        "In MightyCall: Integrations → API. That's where you get the API Key, Client ID and Client Secret. Stored encrypted.",
      ),
      voiceOnly: true,
      signupUrl: "https://www.mightycall.com",
      blurb: t(
        "Solo llamadas (no envía SMS). Suena en tu app de MightyCall y te conecta con el cliente. Firma el acuerdo HIPAA (BAA).",
        "Calls only (no SMS). Rings in your MightyCall app and connects you to the client. It signs the HIPAA agreement (BAA).",
      ),
    },
    {
      id: "QUO",
      name: "Quo (OpenPhone)",
      // Quo authenticates with a single API key — no separate account ID.
      noAccountId: true,
      // Calls happen in Quo's own app (no public dial API), so Deskte can't
      // place them; only SMS routes through Deskte. Hide the "Calls" chip.
      noApiVoice: true,
      tokenLabel: "API Key",
      tokenPlaceholder: "op_...",
      tokenHelp: t(
        "En Quo: Ajustes → API (necesitas plan Business/Scale y ser Owner/Admin). Genera una API Key y pégala aquí. Se guarda cifrada.",
        "In Quo: Settings → API (needs the Business/Scale plan and Owner/Admin). Generate an API Key and paste it here. Stored encrypted.",
      ),
      needsSpace: false,
      signupUrl: "https://www.quo.com",
      blurb: t(
        "SMS de dos vías desde Deskte + llamadas con app propia (iPhone/Android/PC). Las llamadas viven en la app de Quo; el SMS entra y sale desde aquí. Firma el acuerdo HIPAA (BAA) en el plan Business.",
        "Two-way SMS from Deskte + calls with its own app (iPhone/Android/PC). Calls live in the Quo app; SMS flows in and out from here. Signs the HIPAA agreement (BAA) on the Business plan.",
      ),
    },
    {
      id: "GOTO",
      name: "GoTo Connect",
      accountLabel: "Client ID",
      tokenLabel: "Client Secret",
      // The only provider that needs a second step: GoTo authenticates with
      // OAuth, so after saving the keys the agent has to authorize on GoTo.
      oauth: true,
      // NOTE: GoTo delivers inbound SMS through an API we have not confirmed
      // yet, so a client's reply lands in the GoTo app, not in Deskte. The
      // connected card says so — an agent who believes replies arrive here
      // would miss them.
      //
      // GoTo dials from a LINE, not from the number. Found automatically when
      // authorizing; this is the manual escape hatch.
      lineLabel: "Line ID",
      signupUrl: "https://developer.goto.com",
      blurb: t(
        "Teléfono de negocio completo (app, extensiones, IVR) con SMS y llamadas desde Deskte. Es el único que se conecta autorizando en GoTo, no pegando una llave. Firma el acuerdo HIPAA (BAA) — confirma por escrito que cubre voz Y mensajes de texto.",
        "A full business phone (app, extensions, IVR) with SMS and calls from Deskte. The only one connected by authorizing on GoTo rather than pasting a key. It signs the HIPAA agreement (BAA) — get written confirmation that it covers voice AND text messages.",
      ),
    },
  ];

  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [openForm, setOpenForm] = useState(""); // provider id whose form is open
  const [form, setForm] = useState({ phoneNumber: "", accountId: "", authToken: "", spaceUrl: "" });
  // BAA self-attestation. The agency holds its own provider account and signs
  // its own BAA, so the agency owner is the only party who can truthfully
  // declare it — and it is recorded with who + when.
  const [baa, setBaa] = useState({ attestations: [], blocked: [], canAttest: false, callable: [] });
  const [baaBusy, setBaaBusy] = useState("");
  // El registro A2P 10DLC del ENVIO MASIVO. Misma forma exacta que el BAA de
  // arriba, y por la misma razon: el papel esta entre la agencia y su
  // proveedor, y Deskte no puede verlo. Sin el, las operadoras de EE.UU.
  // filtran el envio masivo SIN devolver error.
  const [a2p, setA2p] = useState({ attestations: [], blocked: [], providers: [] });
  const [a2pBusy, setA2pBusy] = useState("");
  // Whose MightyCall line these credentials really are. Pasting a colleague's
  // Client ID/Secret is invisible in this form, and the consequence is silent:
  // the call rings THEM, not you.
  const [ringsAs, setRingsAs] = useState(null);
  // The return URL the agent must register on their own GoTo OAuth client —
  // needed BEFORE they have any credentials to paste here.
  const [gotoRedirectUri, setGotoRedirectUri] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getPhoneIntegrations();
      setIntegrations(data?.integrations || []);
      setRingsAs(data?.ringsAs || null);
      setGotoRedirectUri(data?.gotoRedirectUri || "");
      setBaa({
        attestations: data?.voiceBaaAttestations || [],
        blocked: data?.voiceBaaBlocked || [],
        canAttest: !!data?.canAttestBaa,
        callable: data?.callableProviders || [],
      });
      setA2p({
        attestations: data?.smsA2pAttestations || [],
        blocked: data?.smsA2pBlocked || [],
        providers: data?.a2pProviders || [],
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const byProvider = Object.fromEntries(integrations.map((i) => [i.provider, i]));

  // Retired from the catalog (owner decision, 2026-07-22). Filtered out of the
  // list below, but NOT deleted: an agency that already connected one still
  // sees its card, so the connection stays manageable and disconnectable
  // instead of being stranded with no UI. Dropping it outright would leave a
  // live, PHI-capable integration nobody could turn off.
  const RETIRED = new Set(["SIGNALWIRE"]);

  // Assign / unassign a provider to a channel (exclusive per channel).
  // channel: 'sms' | 'voice'. Toggling the currently-selected one unassigns it.
  const setChannel = async (providerId, channel) => {
    const cur = byProvider[providerId];
    const isOn = channel === "sms" ? cur?.useForSms : cur?.useForVoice;
    setError("");
    try {
      const data = await setPhoneChannels({ [channel]: isOn ? null : providerId });
      setIntegrations(data?.integrations || []);
    } catch (err) { setError(err.message || String(err)); }
  };

  function startConnect(p) {
    const existing = byProvider[p.id];
    setForm({
      phoneNumber: existing?.phoneNumber || "",
      accountId: existing?.accountId || "",
      authToken: "", // never prefilled — write-only
      spaceUrl: existing?.spaceUrl || "",
      jwt: "", // never prefilled — write-only (RingCentral)
    });
    setError("");
    setOpenForm(p.id);
  }

  async function handleSave(p) {
    // Quo authenticates with a single API key — no account ID field.
    const needsAccountId = !p.noAccountId;
    if (!form.phoneNumber.trim() || (needsAccountId && !form.accountId.trim()) || !form.authToken.trim()) {
      setError(needsAccountId
        ? t("Completa el número, el identificador de cuenta y el token.", "Fill in the number, account ID and token.")
        : t("Completa el número y la API Key.", "Fill in the number and API Key."));
      return;
    }
    if (p.needsSpace && !form.spaceUrl.trim()) {
      setError(t("SignalWire necesita la URL del Space (ej. tunombre.signalwire.com).", "SignalWire needs the Space URL (e.g. yourname.signalwire.com)."));
      return;
    }
    if (p.needsJwt && !form.jwt.trim()) {
      setError(t("RingCentral necesita su JWT (credencial server-to-server).", "RingCentral needs its server-to-server JWT credential."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await connectPhoneProvider({
        provider: p.id,
        phoneNumber: form.phoneNumber.trim(),
        // Quo has no account ID; send a constant so the backend's min(1) holds.
        accountId: p.noAccountId ? "quo" : form.accountId.trim(),
        authToken: form.authToken.trim(),
        ...(p.needsSpace ? { spaceUrl: form.spaceUrl.trim() } : {}),
        ...(p.needsJwt ? { jwt: form.jwt.trim() } : {}),
      });
      setOpenForm("");
      setForm({ phoneNumber: "", accountId: "", authToken: "", spaceUrl: "", jwt: "" });
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect(p) {
    const ok = await confirmDialog({
      message: t(
        `¿Desconectar ${p.name}? Se borrarán las credenciales guardadas. Podrás volver a conectarlo cuando quieras.`,
        `Disconnect ${p.name}? The stored credentials will be deleted. You can reconnect anytime.`,
      ),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      await disconnectPhoneProvider(p.id);
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  // ── styles (match WhatsappIntegrationView) ──
  const card = {
    background: "var(--c-card,#0f2433)", border: "1px solid var(--c-border,#1c3a4f)",
    borderRadius: 12, padding: "1.25rem", marginBottom: "1rem",
  };
  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px",
    background: bg, color: fg, border: "none", borderRadius: 8, fontSize: 14,
    fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
  });
  const inp = {
    width: "100%", padding: "9px 11px", borderRadius: 8, fontSize: 14,
    background: "var(--c-surface2,#0b1c2b)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1c3a4f)", boxSizing: "border-box",
  };
  // Channel-selection chip — filled when this provider handles the channel.
  const chip = (active) => ({
    display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px",
    borderRadius: 100, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    background: active ? "rgba(0,212,255,.14)" : "transparent",
    color: active ? "#00d4ff" : "var(--c-text2,#7aa3c0)",
    border: `1px solid ${active ? "rgba(0,212,255,.5)" : "var(--c-border,#1c3a4f)"}`,
  });
  const lbl = { display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 5, color: "var(--c-text2,#7aa3c0)" };

  return (
    <div style={{ padding: "1.5rem", maxWidth: 760, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Phone size={22} style={{ color: "#00d4ff" }} />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          {t("SMS y Llamadas", "SMS & Calls")}
        </h1>
      </div>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: 13, marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
        {t(
          "Conecta tu propia cuenta de un proveedor de telefonía para enviar SMS y registrar llamadas desde Deskte, usando tu propio número. Tú pagas tu uso directo al proveedor; Deskte solo guarda tus llaves cifradas.",
          "Connect your own phone-provider account to send SMS and log calls from Deskte using your own number. You pay your usage directly to the provider; Deskte only stores your encrypted keys.",
        )}
      </p>

      {/* Info banner — what's active once you connect a provider */}
      <div style={{ ...card, display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(0,212,255,.08)", borderColor: "rgba(0,212,255,.35)" }}>
        <Info size={18} style={{ color: "#00d4ff", flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12.5, color: "var(--c-text,#dbeafe)", lineHeight: 1.5 }}>
          {t(
            "Al conectar tu proveedor, el envío y la recepción de SMS quedan activos: escribes y recibes mensajes desde la bandeja de SMS y la ficha del cliente. Las llamadas se marcan con el botón de llamar de la ficha (usa el marcador de tu teléfono) y Deskte registra cada llamada. Solo guardamos tus llaves cifradas; tú pagas tu uso directo al proveedor.",
            "Once you connect your provider, sending and receiving SMS is active: you text and get replies from the SMS inbox and the client profile. Calls are placed with the call button on the profile (using your phone's dialer) and Deskte logs each call. We only store your encrypted keys; you pay your usage directly to the provider.",
          )}
        </div>
      </div>

      {/* What can I text? — plain-language HIPAA guidance (draft, pending legal). */}
      <SmsContentGuide lang={lang} defaultOpen={true} />

      {error && (
        <div style={{ ...card, background: "#3a1620", borderColor: "#7f1d1d", color: "#fecaca" }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={16} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      ) : (
        <>
          {PROVIDERS.filter((p) => !RETIRED.has(p.id) || byProvider[p.id]).map((p) => {
            const conn = byProvider[p.id];
            const isOpen = openForm === p.id;
            return (
              <div key={p.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15.5 }}>{p.name}</span>
                      {p.recommended && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#00d4ff", background: "rgba(0,212,255,.12)", border: "1px solid rgba(0,212,255,.3)", borderRadius: 100, padding: "1px 9px" }}>
                          {t("Recomendado", "Recommended")}
                        </span>
                      )}
                      {conn && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, color: "#34d399", background: "rgba(16,185,129,.12)", border: "1px solid rgba(16,185,129,.3)", borderRadius: 100, padding: "1px 9px" }}>
                          <Check size={12} /> {t("Conectado", "Connected")}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.45 }}>
                      {conn ? (
                        <span>{p.noAccountId ? conn.phoneMasked : `${p.accountLabel}: ${conn.accountId} · ${conn.phoneMasked}`}</span>
                      ) : (
                        p.blurb
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    {!isOpen && (
                      <button onClick={() => startConnect(p)} disabled={busy} style={btn(conn ? "transparent" : "#2563eb", conn ? "#7aa3c0" : "#fff")}>
                        {conn ? <RefreshCw size={16} /> : <Plug size={16} />}
                        {conn ? t("Reconectar", "Reconnect") : t("Conectar", "Connect")}
                      </button>
                    )}
                    {conn && !isOpen && (
                      <button onClick={() => handleDisconnect(p)} disabled={busy} style={btn("transparent", "#fca5a5")}>
                        <Trash2 size={16} /> {t("Desconectar", "Disconnect")}
                      </button>
                    )}
                  </div>
                </div>

                {/* Per-channel routing — pick this provider for SMS / Calls */}
                {conn && !isOpen && (
                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text3,#45688a)" }}>{t("Usar para:", "Use for:")}</span>
                    {!p.voiceOnly && (
                      <button onClick={() => setChannel(p.id, "sms")} title={t("Enviar SMS por este proveedor", "Send SMS through this provider")}
                        style={chip(conn.useForSms)}>
                        <MessageSquare size={13} /> SMS {conn.useForSms ? "✓" : ""}
                      </button>
                    )}
                    {!p.noApiVoice && (
                      <button onClick={() => setChannel(p.id, "voice")} title={t("Hacer llamadas por este proveedor", "Make calls through this provider")}
                        style={chip(conn.useForVoice)}>
                        <Phone size={13} /> {t("Llamadas", "Calls")} {conn.useForVoice ? "✓" : ""}
                      </button>
                    )}
                    {p.noApiVoice && (
                      <span style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)" }}>
                        {t("(las llamadas se hacen en la app de Quo)", "(calls are made in the Quo app)")}
                      </span>
                    )}
                  </div>
                )}

                {/* Inline connect form */}
                {isOpen && (
                  <div style={{ marginTop: 14, borderTop: "1px solid var(--c-border,#1c3a4f)", paddingTop: 14, display: "grid", gap: 12 }}>
                    <div>
                      <label style={lbl}>{t("Número de teléfono", "Phone number")}</label>
                      <input style={inp} placeholder="+1 305 555 0123" value={form.phoneNumber}
                        onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))} />
                    </div>
                    {!p.noAccountId && (
                      <div>
                        <label style={lbl}>{p.accountLabel}</label>
                        <input style={inp} value={form.accountId}
                          onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))} />
                      </div>
                    )}
                    {p.needsSpace && (
                      <div>
                        <label style={lbl}>Space URL</label>
                        <input style={inp} placeholder="yourname.signalwire.com" value={form.spaceUrl}
                          onChange={(e) => setForm((f) => ({ ...f, spaceUrl: e.target.value }))} />
                      </div>
                    )}
                    <div>
                      <label style={lbl}>{p.tokenLabel}</label>
                      <input style={inp} type="password" autoComplete="off" placeholder={p.tokenPlaceholder || "••••••••••••••••"} value={form.authToken}
                        onChange={(e) => setForm((f) => ({ ...f, authToken: e.target.value }))} />
                      <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 5 }}>
                        {p.tokenHelp || t("Se guarda cifrado (AES-256). Nunca se vuelve a mostrar.", "Stored encrypted (AES-256). Never shown again.")}
                      </div>
                    </div>
                    {p.needsJwt && (
                      <div>
                        <label style={lbl}>{p.jwtLabel || "JWT"}</label>
                        <textarea style={{ ...inp, minHeight: 70, fontFamily: "var(--font-mono,monospace)", resize: "vertical" }} autoComplete="off"
                          placeholder={p.jwtPlaceholder || "eyJ..."} value={form.jwt}
                          onChange={(e) => setForm((f) => ({ ...f, jwt: e.target.value }))} />
                        <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 5 }}>
                          {p.jwtHelp || t("La obtienes en el panel de RingCentral Developers (app server-to-server → Credentials → JWT). Se guarda cifrada.", "Get it in the RingCentral Developers console (server-to-server app → Credentials → JWT). Stored encrypted.")}
                        </div>
                      </div>
                    )}
                    {p.oauth && gotoRedirectUri && (
                      <CopyableUrl
                        url={gotoRedirectUri}
                        lang={lang}
                        title={t("1) Pega esta URL de retorno en GoTo", "1) Paste this return URL into GoTo")}
                        help={t(
                          "En GoTo Developer Center → tu OAuth client → Redirect URI. Tiene que quedar EXACTA, o GoTo rechaza la autorización. Después copia de ahí el Client ID y el Client Secret y guárdalos aquí.",
                          "In GoTo Developer Center → your OAuth client → Redirect URI. It has to match EXACTLY, or GoTo refuses the authorization. Then copy the Client ID and Client Secret from there and save them here.",
                        )}
                      />
                    )}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <button onClick={() => handleSave(p)} disabled={busy} style={btn("#16a34a")}>
                        {busy ? <Loader2 size={16} className="dkt-spin" /> : <Check size={16} />}
                        {t("Guardar conexión", "Save connection")}
                      </button>
                      <button onClick={() => { setOpenForm(""); setError(""); }} disabled={busy} style={btn("transparent", "#7aa3c0")}>
                        {t("Cancelar", "Cancel")}
                      </button>
                      <a href={p.signupUrl} target="_blank" rel="noreferrer"
                        style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#00d4ff", textDecoration: "none" }}>
                        <ExternalLink size={14} /> {t(`Abrir ${p.name}`, `Open ${p.name}`)}
                      </a>
                    </div>
                  </div>
                )}

                {/* BAA self-attestation — only for a connected provider Deskte
                    can actually dial. Unticked, the agent keeps the tel: link
                    exactly as before; nothing is taken away. */}
                {/* Which MightyCall user these credentials belong to. Nothing
                    in the form reveals it, and the consequence is invisible
                    until a call rings the wrong person's phone. */}
                {conn && p.id === "MIGHTYCALL" && ringsAs && !isOpen && (
                  <div style={{ marginTop: 10, padding: "8px 11px", borderRadius: 9, border: "1px solid #1e293b", fontSize: 12.5, color: "#cbd5e1", display: "flex", gap: 8 }}>
                    <Info size={14} style={{ color: "#60a5fa", flexShrink: 0, marginTop: 2 }} />
                    <span>
                      {t("Las llamadas sonarán en la línea de ", "Calls will ring the line of ")}
                      <strong style={{ color: "#e2e8f0" }}>
                        {[ringsAs.name, ringsAs.extension ? `ext ${ringsAs.extension}` : ""].filter(Boolean).join(" — ")}
                      </strong>
                      {t(
                        ". Si no eres tú, conecta TUS propias credenciales de MightyCall: esa persona tendría que contestar cada llamada tuya.",
                        ". If that isn't you, connect YOUR own MightyCall credentials — otherwise that person has to answer every call you place.",
                      )}
                    </span>
                  </div>
                )}

                {conn && baa.callable.includes(p.id) && !isOpen && (
                  <BaaAttestation
                    provider={p} conn={conn} baa={baa} busy={baaBusy === p.id} lang={lang}
                    onToggle={async (attested) => {
                      setBaaBusy(p.id); setError("");
                      try { await setOwnVoiceBaa(p.id, attested); await refresh(); }
                      catch (err) { setError(err.message || String(err)); }
                      finally { setBaaBusy(""); }
                    }}
                  />
                )}

                {/* Registro A2P 10DLC — solo para un proveedor conectado por
                    el que Deskte manda SMS de verdad. Sin marcar, el SMS de
                    uno en uno sigue funcionando igual: lo unico que queda
                    apagado es el envio MASIVO. */}
                {conn && a2p.providers.includes(p.id) && !isOpen && (
                  <A2pAttestation
                    provider={p} conn={conn} a2p={a2p} canAttest={baa.canAttest}
                    busy={a2pBusy === p.id} lang={lang}
                    onToggle={async (attested) => {
                      setA2pBusy(p.id); setError("");
                      try { await attestSmsA2p(p.id, attested); await refresh(); }
                      catch (err) { setError(err.message || String(err)); }
                      finally { setA2pBusy(""); }
                    }}
                  />
                )}

                {/* Auto-link on reply — Twilio + SignalWire, when connected */}
                {conn && (p.id === "TWILIO" || p.id === "SIGNALWIRE") && !isOpen && (
                  <ProviderAutoLink provider={p.id} conn={conn} lang={lang} onChanged={refresh} />
                )}

                {/* Quo — the inbound webhook URL to paste in the Quo dashboard
                    so client replies arrive in Deskte. */}
                {conn && p.id === "QUO" && !isOpen && conn.inboundWebhookUrl && (
                  <QuoInboundHelp url={conn.inboundWebhookUrl} lang={lang} active={!!conn.autoLinkOnReply} onChanged={refresh} />
                )}

                {/* GoTo — the second step. Saved credentials alone send
                    nothing: the agent has to authorize on GoTo's own screen. */}
                {conn && p.oauth && !isOpen && (
                  <GotoAuthorize conn={conn} lang={lang} onChanged={refresh} lineLabel={p.lineLabel} />
                )}
              </div>
            );
          })}

          {/* Cost note */}
          <div style={{ ...card, display: "flex", gap: 10, alignItems: "flex-start", background: "var(--c-surface2,#0d2030)", borderColor: "var(--c-border,#1c3a4f)" }}>
            <Info size={18} style={{ color: "#38bdf8", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12.5, color: "var(--c-text2,#9fc1da)", lineHeight: 1.5 }}>
              {/* El texto sale de `avisoDeCostoSms.js`, que es donde vive el
                  precio. Escrito aqui a mano, la pantalla del envio masivo
                  acabaria diciendo otro numero (regla de oro 52). */}
              {costoAproximadoDeLaLinea(lang === "es" ? "es" : "en")}
            </div>
          </div>

          {/* HIPAA note */}
          <div style={{ ...card, display: "flex", gap: 10, alignItems: "flex-start", background: "#1a2433", borderColor: "#334155" }}>
            <ShieldCheck size={18} style={{ color: "#34d399", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.5 }}>
              {t(
                "SMS y llamadas por Twilio, RingCentral, MightyCall, Quo o GoTo SÍ pueden llevar datos médicos, siempre que firmes el acuerdo HIPAA (BAA) con tu proveedor. Ojo: firmar el BAA suele tener un costo mensual (GoTo lo incluye en sus Términos, los demás no); confirma por escrito que cubre lo que vas a usar — voz y mensajes de texto — antes de mandar datos médicos. Sin el BAA, textea solo logística (sin info de salud). (WhatsApp nunca: Meta no lo firma.)",
                "SMS and calls via Twilio, RingCentral, MightyCall, Quo or GoTo CAN carry medical data, as long as you sign the HIPAA agreement (BAA) with your provider. Note: signing the BAA usually carries a monthly cost (GoTo includes it in their Terms, the others don't); get written confirmation that it covers what you'll use — voice and text messages — before sending any medical data. Without the BAA, text logistics only (no health info). (WhatsApp never: Meta won't sign one.)",
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// BAA self-attestation for ONE connected provider.
//
// Why the agency and not the platform owner: in the BYO model the agency
// contracts with the provider under its own account and signs its own BAA.
// The platform owner cannot see that contract, so having her tick a box per
// agency was an attestation about someone else's paperwork — and it did not
// survive a few hundred agencies. Here the person who actually signed says so,
// and we record who + when, which is the evidence trail counsel asks for.
//
// Declining is a complete, supported state: the agent keeps the tel: dialer,
// which routes no PHI through the provider at all.
function BaaAttestation({ provider, conn, baa, busy, lang, onToggle }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const att = (baa.attestations || []).find((a) => a.provider === provider.id);
  const isBlocked = (baa.blocked || []).includes(provider.id);
  const on = !!att;

  const box = {
    marginTop: 12, padding: "10px 12px", borderRadius: 10,
    border: `1px solid ${isBlocked ? "rgba(255,61,107,.35)" : on ? "rgba(52,211,153,.35)" : "#1e293b"}`,
    background: isBlocked ? "rgba(255,61,107,.06)" : on ? "rgba(52,211,153,.06)" : "transparent",
  };

  return (
    <div style={box}>
      <label style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: busy ? "wait" : "pointer" }}>
        <input
          type="checkbox" checked={on} disabled={busy || !baa.canAttest}
          onChange={(e) => onToggle(e.target.checked)}
          style={{ marginTop: 2, width: 15, height: 15, accentColor: "#34d399", cursor: "inherit" }}
        />
        <span style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.5 }}>
          {t(
            `Confirmo que mi agencia tiene un acuerdo HIPAA (BAA) firmado con ${provider.name}.`,
            `I confirm my agency has a signed HIPAA agreement (BAA) with ${provider.name}.`,
          )}
          <span style={{ display: "block", color: "#64748b", marginTop: 3 }}>
            {on
              ? t(
                "Con esto, las llamadas salen DENTRO de Deskte, por el numero de la agencia, y quedan registradas.",
                "With this on, calls are placed INSIDE Deskte, from the agency number, and are logged.",
              )
              : t(
                "Sin marcar, el boton Llamar sigue funcionando igual que siempre: abre la app del telefono. No pasa ningun dato por el proveedor.",
                "Unticked, the Call button keeps working exactly as before: it opens the phone app. No data goes through the provider.",
              )}
          </span>
        </span>
      </label>

      {!baa.canAttest && (
        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6, paddingLeft: 24 }}>
          {t("Solo el dueno de la agencia puede declararlo.", "Only the agency owner can declare this.")}
        </div>
      )}

      {att && (
        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6, paddingLeft: 24 }}>
          {t("Declarado por", "Declared by")} <strong style={{ color: "#94a3b8" }}>{att.byName}</strong>
          {att.at ? ` — ${new Date(att.at).toLocaleDateString("en-US")}` : ""}
        </div>
      )}

      {/* The agency's own declaration can be overridden by the platform. Say so
          plainly: a ticked box that silently does nothing is worse than none. */}
      {isBlocked && (
        <div style={{ fontSize: 11.5, color: "#fda4af", marginTop: 6, paddingLeft: 24, display: "flex", gap: 6 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{t(
            "Deskte tiene las llamadas en la app desactivadas para esta agencia. Escribe a soporte.",
            "Deskte has in-app calling switched off for this agency. Contact support.",
          )}</span>
        </div>
      )}

      {/* Attested + not blocked, but not routed to voice: the box is ticked and
          the button still will not appear. Name the missing step. */}
      {on && !isBlocked && !conn.useForVoice && (
        <div style={{ fontSize: 11.5, color: "#fbbf24", marginTop: 6, paddingLeft: 24 }}>
          {t(
            'Falta un paso: pon este proveedor en "Llamadas" arriba.',
            'One step left: set this provider to "Calls" above.',
          )}
        </div>
      )}
    </div>
  );
}

// EL REGISTRO A2P 10DLC de UN proveedor conectado — la compuerta del envio
// masivo de SMS.
//
// Por que existe: en Estados Unidos, mandar textos en volumen desde un numero
// comercial exige que ese numero este registrado en A2P 10DLC. Sin ese
// registro las operadoras NO devuelven un error: filtran el mensaje en
// silencio. La agencia leeria "enviado: 800" y a los clientes no les llegaria
// nada, y nadie se enteraria hasta que alguien llamara preguntando.
//
// Por que lo declara la agencia y no Deskte: el registro vive en la cuenta del
// proveedor de la agencia (modelo BYO) y la plataforma no tiene acceso. Es la
// misma situacion que el BAA de voz de arriba, y por eso es la misma forma.
//
// Y lo importante de decir en voz alta: SIN MARCAR NO SE PIERDE NADA de lo que
// hoy funciona. El SMS de uno en uno, contestar en Conversaciones, el enlace
// de renovacion — todo sigue igual. Lo unico que queda apagado es el boton de
// envio masivo en Email Masivo.
function A2pAttestation({ provider, conn, a2p, canAttest, busy, lang, onToggle }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const att = (a2p.attestations || []).find((a) => a.provider === provider.id);
  const isBlocked = (a2p.blocked || []).includes(provider.id);
  const on = !!att;

  const box = {
    marginTop: 12, padding: "10px 12px", borderRadius: 10,
    border: `1px solid ${isBlocked ? "rgba(255,61,107,.35)" : on ? "rgba(52,211,153,.35)" : "#1e293b"}`,
    background: isBlocked ? "rgba(255,61,107,.06)" : on ? "rgba(52,211,153,.06)" : "transparent",
  };

  return (
    <div style={box}>
      <label style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: busy ? "wait" : "pointer" }}>
        <input
          type="checkbox" checked={on} disabled={busy || !canAttest}
          onChange={(e) => onToggle(e.target.checked)}
          style={{ marginTop: 2, width: 15, height: 15, accentColor: "#34d399", cursor: "inherit" }}
        />
        <span style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.5 }}>
          {t(
            `Confirmo que el número de ${provider.name} está registrado en A2P 10DLC.`,
            `I confirm my ${provider.name} number is registered for A2P 10DLC.`,
          )}
          <span style={{ display: "block", color: "#64748b", marginTop: 3 }}>
            {on
              ? t(
                "Con esto se enciende el envío MASIVO de SMS en Email Masivo.",
                "This turns on BULK SMS sending in Mass Email.",
              )
              : t(
                "Sin marcar, el envío MASIVO de SMS queda apagado. Mandarle un texto a un cliente desde su ficha sigue funcionando igual: 10DLC regula el volumen, no la conversación.",
                "Unticked, BULK SMS stays off. Texting a client from their record keeps working exactly as before: 10DLC governs volume, not conversation.",
              )}
          </span>
        </span>
      </label>

      {!canAttest && (
        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6, paddingLeft: 24 }}>
          {t("Solo el dueno de la agencia puede declararlo.", "Only the agency owner can declare this.")}
        </div>
      )}

      {att && (
        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6, paddingLeft: 24 }}>
          {t("Declarado por", "Declared by")} <strong style={{ color: "#94a3b8" }}>{att.byName}</strong>
          {att.at ? ` — ${new Date(att.at).toLocaleDateString("en-US")}` : ""}
        </div>
      )}

      {/* La declaracion de la agencia la puede apagar la plataforma. Decirlo:
          una casilla marcada que no hace nada es peor que ninguna. */}
      {isBlocked && (
        <div style={{ fontSize: 11.5, color: "#fda4af", marginTop: 6, paddingLeft: 24, display: "flex", gap: 6 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{t(
            "Deskte tiene el envio masivo de SMS desactivado para esta agencia. Escribe a soporte.",
            "Deskte has bulk SMS switched off for this agency. Contact support.",
          )}</span>
        </div>
      )}

      {/* Declarado y sin bloquear, pero el proveedor no esta puesto en "SMS":
          la casilla esta marcada y el boton sigue sin aparecer. */}
      {on && !isBlocked && !conn.useForSms && (
        <div style={{ fontSize: 11.5, color: "#fbbf24", marginTop: 6, paddingLeft: 24 }}>
          {t(
            'Falta un paso: pon este proveedor en "SMS" arriba.',
            'One step left: set this provider to "SMS" above.',
          )}
        </div>
      )}
    </div>
  );
}

// Auto-link on reply (renewal) — turn ON/OFF. ON points the provider number's
// inbound webhook at us (via the provider's API) so a client's reply auto-fires
// the renewal link. Shown on the connected Twilio / SignalWire cards.
function ProviderAutoLink({ provider, conn, lang, onChanged }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [busy, setBusy] = useState(false);
  const on = !!conn.autoLinkOnReply;
  async function toggle() {
    setBusy(true);
    try {
      if (on) {
        await disablePhoneInbound(provider);
        toast.success(t("Respuestas automáticas desactivadas.", "Auto-replies turned off."));
      } else {
        await configurePhoneInbound(provider);
        toast.success(t("¡Listo! El link se enviará solo cuando el cliente responda.", "Done! The link will auto-send when the client replies."));
      }
      await onChanged?.();
    } catch (err) {
      toast.error(err.message || t("No se pudo cambiar.", "Couldn't change it."));
    } finally { setBusy(false); }
  }
  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--c-border,#1c3a4f)", paddingTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14 }}>
            🔁 {t("Respuestas automáticas (renovación)", "Auto-replies (renewal)")}
            {on && (
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "#34d399", background: "rgba(16,185,129,.12)", border: "1px solid rgba(16,185,129,.3)", borderRadius: 100, padding: "1px 9px" }}>
                {t("Activo", "On")}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.45, marginTop: 4, maxWidth: 520 }}>
            {t(
              "Cuando el cliente RESPONDE al aviso de renovación, el sistema le envía el link solo — sin que lo hagas a mano. (Configura el webhook de tu número automáticamente.)",
              "When the client REPLIES to the renewal notice, the system auto-sends them the link — no manual step. (Configures your number's webhook automatically.)",
            )}
          </div>
        </div>
        <button onClick={toggle} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 9, border: "1px solid", borderColor: on ? "#ef4444" : "#16a34a", background: on ? "transparent" : "#16a34a", color: on ? "#fca5a5" : "#fff", fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer", flexShrink: 0 }}>
          {busy ? <Loader2 size={15} className="dkt-spin" /> : null}
          {on ? t("Desactivar", "Turn off") : t("Activar", "Turn on")}
        </button>
      </div>
    </div>
  );
}

// Quo inbound help — Quo doesn't expose an API to set the webhook, so the agent
// pastes this URL into their Quo dashboard (Settings → Webhooks → new webhook →
// event "message.received"). Shown on the connected Quo card.
// `active` comes from the saved integration (autoLinkOnReply), so the "Activado"
// badge survives a reload — `done` alone only knew about a click in this session,
// which made an already-configured account look unconfigured every time the
// screen was reopened.
function QuoInboundHelp({ url, lang, active = false, onChanged }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const isOn = active || done;

  async function activate() {
    setBusy(true);
    try {
      const data = await configureQuoWebhooks();
      setDone(true);
      await onChanged?.();
      const calls = data?.calls, summaries = data?.summaries;
      if (calls && summaries) {
        toast.success(t("¡Listo! Recibirás SMS y se registrarán las llamadas (con resumen).", "Done! You'll receive SMS and calls will be logged (with summary)."));
      } else if (calls) {
        toast.success(t("¡Listo! SMS y registro de llamadas activados. (El resumen con IA no está en tu plan de Quo.)", "Done! SMS and call logging on. (AI summary isn't on your Quo plan.)"));
      } else {
        toast.success(t("¡Listo! Recibirás SMS. (El registro de llamadas no está disponible en tu plan de Quo.)", "Done! You'll receive SMS. (Call logging isn't available on your Quo plan.)"));
      }
    } catch (err) {
      toast.error(err.message || t("No se pudo activar. Revisa que tu API Key sea de un plan con API.", "Couldn't activate. Check that your API Key is from a plan with API access."));
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--c-border,#1c3a4f)", paddingTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
        📥 {t("Recibir SMS y registrar llamadas", "Receive SMS & log calls")}
        {isOn && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#34d399", background: "rgba(16,185,129,.12)", border: "1px solid rgba(16,185,129,.3)", borderRadius: 100, padding: "1px 9px" }}>
            {t("Activado", "On")}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.45, marginBottom: 12, maxWidth: 560 }}>
        {t(
          "Un clic y Deskte configura todo en tu cuenta de Quo (SMS entrantes + registro de llamadas con resumen). No tienes que tocar el panel de Quo.",
          "One click and Deskte sets everything up in your Quo account (inbound SMS + call logging with summary). No need to touch the Quo dashboard.",
        )}
      </div>
      <button onClick={activate} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 9, border: "none", background: isOn ? "transparent" : "#16a34a", color: isOn ? "#34d399" : "#fff", fontWeight: 700, fontSize: 13.5, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, borderWidth: isOn ? 1 : 0, borderStyle: "solid", borderColor: isOn ? "#34d399" : "transparent" }}>
        {busy ? <Loader2 size={16} className="dkt-spin" /> : isOn ? <RefreshCw size={16} /> : <Plug size={16} />}
        {busy ? t("Activando…", "Activating…") : isOn ? t("Volver a sincronizar", "Re-sync") : t("Activar recepción", "Turn on receiving")}
      </button>

      {/* Manual fallback — only if the button won't work for them. */}
      <div style={{ marginTop: 10 }}>
        <button onClick={() => setShowManual((s) => !s)} style={{ background: "transparent", border: "none", color: "var(--c-text3,#45688a)", fontSize: 11.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
          {t("¿Prefieres hacerlo a mano en Quo?", "Rather do it by hand in Quo?")}
        </button>
        {showManual && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.45, marginBottom: 8, maxWidth: 560 }}>
              {t(
                "En Quo → Ajustes → Webhooks, crea webhooks para message.received, call.completed, call.recording.completed y call.summary.completed, todos con esta URL:",
                "In Quo → Settings → Webhooks, create webhooks for message.received, call.completed, call.recording.completed and call.summary.completed, all with this URL:",
              )}
            </div>
            <CopyableUrl url={url} lang={lang} />
          </div>
        )}
      </div>
    </div>
  );
}

// A read-only URL the agent has to paste somewhere else, with a copy button.
// Shared by the Quo webhook help and the GoTo return URL — the same widget was
// written twice otherwise, and a URL you cannot copy cleanly is a support call.
function CopyableUrl({ url, lang, title, help }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("Copiado.", "Copied."));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("No se pudo copiar.", "Couldn't copy."));
    }
  }
  return (
    <div>
      {title && (
        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 5, color: "var(--c-text2,#7aa3c0)" }}>{title}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <code style={{ flex: 1, minWidth: 220, fontSize: 12, background: "var(--c-surface2,#0b1c2b)", border: "1px solid var(--c-border,#1c3a4f)", borderRadius: 8, padding: "8px 10px", color: "var(--c-white,#eef6ff)", overflowX: "auto", whiteSpace: "nowrap" }}>
          {url}
        </code>
        <button onClick={copy} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border,#1c3a4f)", background: "transparent", color: copied ? "#34d399" : "#00d4ff", fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 }}>
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? t("Copiado", "Copied") : t("Copiar", "Copy")}
        </button>
      </div>
      {help && (
        <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 6, lineHeight: 1.45 }}>{help}</div>
      )}
    </div>
  );
}

// GoTo Connect — the authorize step, and the two things that silently stop it
// from working afterwards.
//
// Why this exists at all: every other provider here is done the moment the
// credentials are saved. GoTo is not — its API takes an OAuth token, so a card
// that said "Connected" after the save would be lying, and the agent would only
// find out when a text failed. So the card says what is missing and offers the
// one button that fixes it.
function GotoAuthorize({ conn, lang, onChanged, lineLabel = "Line ID" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [busy, setBusy] = useState(false);
  const [editingLine, setEditingLine] = useState(false);
  const [lineValue, setLineValue] = useState(conn.externalLineId || "");
  const [savingLine, setSavingLine] = useState(false);
  const authorized = !conn.needsAuthorization;
  const missingLine = authorized && !conn.externalLineId;

  async function saveLine() {
    setSavingLine(true);
    try {
      await setGotoLine(lineValue.trim());
      setEditingLine(false);
      toast.success(t("Línea guardada.", "Line saved."));
      await onChanged?.();
    } catch (err) {
      toast.error(err.message || t("No se pudo guardar.", "Couldn't save it."));
    } finally {
      setSavingLine(false);
    }
  }

  // The popup posts its result back (kind:'phone'), same as the mailbox and
  // calendar flows.
  useEffect(() => {
    function onMsg(ev) {
      if (ev.origin !== window.location.origin) return;
      if (!ev?.data || ev.data.type !== "deskte-oauth-result" || ev.data.kind !== "phone") return;
      setBusy(false);
      if (ev.data.status === "success") {
        if (ev.data.line === "missing") {
          toast.error(t(
            "GoTo quedó autorizado, pero no encontramos la línea de tu número. Pégala a mano en Reconectar.",
            "GoTo is authorized, but we couldn't find the line for your number. Paste it by hand under Reconnect.",
          ));
        } else {
          toast.success(t("GoTo autorizado. Ya puedes textear y llamar.", "GoTo authorized. You can text and call now."));
        }
        onChanged?.();
      } else {
        toast.error(t("No se pudo autorizar: ", "Couldn't authorize: ") + (ev.data.error || ""));
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [lang, onChanged]);

  async function authorize() {
    setBusy(true);
    try {
      const { authUrl } = await startGotoAuthorization();
      const popup = window.open(authUrl, "deskte-goto-oauth", "width=560,height=720,menubar=no,toolbar=no");
      if (!popup) window.location.href = authUrl; // popup blocked → full redirect
    } catch (err) {
      toast.error(err.message || t("No se pudo empezar la autorización.", "Couldn't start the authorization."));
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--c-border,#1c3a4f)", paddingTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14 }}>
            🔐 {t("Autorización de GoTo", "GoTo authorization")}
            <span style={{
              fontSize: 11.5, fontWeight: 700, borderRadius: 100, padding: "1px 9px",
              color: authorized ? "#34d399" : "#fbbf24",
              background: authorized ? "rgba(16,185,129,.12)" : "rgba(251,191,36,.12)",
              border: `1px solid ${authorized ? "rgba(16,185,129,.3)" : "rgba(251,191,36,.35)"}`,
            }}>
              {authorized ? t("Autorizado", "Authorized") : t("Falta autorizar", "Not authorized yet")}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.45, marginTop: 4, maxWidth: 520 }}>
            {authorized
              ? t(
                "Tu cuenta de GoTo está autorizada. Deskte renueva el permiso solo; si alguna vez lo revocas en GoTo, este botón vuelve a aparecer.",
                "Your GoTo account is authorized. Deskte renews the permission on its own; if you ever revoke it in GoTo, this button comes back.",
              )
              : t(
                "Guardaste las llaves, pero falta el permiso: GoTo pide que lo apruebes en su pantalla. Sin esto no sale ningún SMS ni ninguna llamada.",
                "The keys are saved, but the permission isn't: GoTo needs you to approve it on their screen. Until then no SMS and no call goes out.",
              )}
          </div>
        </div>
        <button onClick={authorize} disabled={busy} style={{
          display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 9,
          border: authorized ? "1px solid #34d399" : "none",
          background: authorized ? "transparent" : "#16a34a",
          color: authorized ? "#34d399" : "#fff",
          fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer", flexShrink: 0, opacity: busy ? 0.7 : 1,
        }}>
          {busy ? <Loader2 size={15} className="dkt-spin" /> : authorized ? <RefreshCw size={15} /> : <Plug size={15} />}
          {authorized ? t("Volver a autorizar", "Authorize again") : t("Autorizar con GoTo", "Authorize with GoTo")}
        </button>
      </div>

      {/* Authorized, and still unable to call: GoTo dials from a line we never
          found. Naming the missing piece beats a call that fails later. */}
      {missingLine && !editingLine && (
        <div style={{ fontSize: 11.5, color: "#fbbf24", marginTop: 10, display: "flex", gap: 6, alignItems: "flex-start" }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            {t(
              "Falta la línea desde la que se marca: no encontramos ninguna con tu número. Las llamadas no saldrán hasta que la pongas.",
              "The line calls go out from is missing: we found none carrying your number. Calls won't go out until you set it.",
            )}{" "}
            <button onClick={() => setEditingLine(true)} style={{ background: "transparent", border: "none", color: "#00d4ff", fontSize: 11.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
              {t("Ponerla a mano", "Set it by hand")}
            </button>
          </span>
        </div>
      )}

      {/* The line, once there is one: shown, and changeable without touching
          the credentials (editing it through Reconnect would cost a whole
          re-authorization). */}
      {authorized && conn.externalLineId && !editingLine && (
        <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 10 }}>
          {lineLabel}: <code style={{ color: "var(--c-text2,#7aa3c0)" }}>{conn.externalLineId}</code>{" "}
          <button onClick={() => { setLineValue(conn.externalLineId || ""); setEditingLine(true); }}
            style={{ background: "transparent", border: "none", color: "#00d4ff", fontSize: 11.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            {t("cambiar", "change")}
          </button>
        </div>
      )}

      {editingLine && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={lineValue}
            onChange={(e) => setLineValue(e.target.value)}
            placeholder={lineLabel}
            style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 8, fontSize: 13, background: "var(--c-surface2,#0b1c2b)", color: "var(--c-white,#eef6ff)", border: "1px solid var(--c-border,#1c3a4f)" }}
          />
          <button onClick={saveLine} disabled={savingLine} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: 13, cursor: savingLine ? "wait" : "pointer" }}>
            {savingLine ? <Loader2 size={14} className="dkt-spin" /> : <Check size={14} />} {t("Guardar", "Save")}
          </button>
          <button onClick={() => setEditingLine(false)} disabled={savingLine} style={{ background: "transparent", border: "none", color: "var(--c-text2,#7aa3c0)", fontSize: 12.5, cursor: "pointer" }}>
            {t("Cancelar", "Cancel")}
          </button>
        </div>
      )}

      {/* What does NOT work yet, said here rather than discovered by a client
          whose reply nobody answered. */}
      <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 10, display: "flex", gap: 6, alignItems: "flex-start" }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          {t(
            "Por ahora con GoTo los mensajes SALEN desde Deskte, pero las RESPUESTAS del cliente llegan a la app de GoTo, no aquí. Si necesitas la conversación completa dentro del CRM, usa Quo o Twilio para el SMS.",
            "For now GoTo sends messages OUT from Deskte, but the client's REPLIES arrive in the GoTo app, not here. If you need the whole conversation inside the CRM, use Quo or Twilio for SMS.",
          )}
        </span>
      </div>
    </div>
  );
}

// =====================================================================
// SmsContentGuide — collapsible "what can I text?" reference panel.
//
// Plain-language HIPAA guidance an agent reads BEFORE writing an SMS:
//   • Logistics ("can we talk?", appointment reminders) = always fine.
//   • Anything tying a person to their HEALTH or health plan = PHI =
//     needs a signed BAA with the provider (expensive). Health / ACA /
//     Medicare are PHI; auto / home / business are NOT; life = caution.
//   • Golden rule: the SMS only INVITES; sensitive detail goes by call,
//     secure portal, or in person.
//
// The wording is a DRAFT pending legal review — the footer says so and
// the text lives in this one place so the attorney's final copy can
// replace it. Exported so the Phase-2 SMS compose screen can reuse it.
// =====================================================================
export function SmsContentGuide({ lang = "en", defaultOpen = false }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [open, setOpen] = useState(defaultOpen);

  const shell = {
    background: "var(--c-card,#0f2433)", border: "1px solid var(--c-border,#1c3a4f)",
    borderRadius: 12, marginBottom: "1rem", overflow: "hidden",
  };
  const headBtn = {
    width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "13px 15px",
    background: "transparent", border: "none", color: "var(--c-white,#eef6ff)",
    fontSize: 14.5, fontWeight: 700, cursor: "pointer", textAlign: "left",
  };
  const body = { padding: "0 15px 15px", display: "grid", gap: 14 };
  const exItem = (color) => ({
    display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5,
    color: "var(--c-text2,#9fc1da)", lineHeight: 1.45,
  });
  const heading = { fontSize: 12.5, fontWeight: 700, marginBottom: 7, letterSpacing: 0.2 };

  // Example messages — kept short and concrete; clients are texted in Spanish.
  const safe = [
    t("«¿Tienes un minuto para hablar hoy?»", "“Do you have a minute to talk today?”"),
    t("«Te llamo a las 3pm, ¿te queda bien?»", "“I'll call you at 3pm, does that work?”"),
    t("«Recordatorio: tienes cita conmigo mañana 10am.»", "“Reminder: you have an appointment with me tomorrow 10am.”"),
    t("«Tu documento está listo para firmar, entra a tu portal.»", "“Your document is ready to sign, open your portal.”"),
    t("«Tengo novedades sobre tu cobertura, ¿te llamo?»", "“I have news about your coverage, can I call you?”"),
  ];
  const phi = [
    t("«Tu plan ACA / Obamacare ya está activo.»", "“Your ACA / Obamacare plan is now active.”"),
    t("«Te aprobaron el subsidio de $320 en tu plan de salud.»", "“You were approved for a $320 subsidy on your health plan.”"),
    t("«Tu Member ID del plan médico es…»", "“Your health-plan Member ID is…”"),
    t("«Por tu condición, el plan que te conviene es…»", "“Given your condition, the plan that suits you is…”"),
    t("Foto de una tarjeta del seguro médico o documento clínico.", "A photo of a health-insurance card or medical document."),
  ];
  // Per-line-of-business PHI status — the distinction that matters most.
  const ramos = [
    { ico: "✅", label: t("Auto", "Auto"), note: t("no es PHI — puedes dar detalle", "not PHI — detail is fine") },
    { ico: "✅", label: t("Hogar / Inquilino", "Home / Renters"), note: t("no es PHI", "not PHI") },
    { ico: "✅", label: t("Negocio / Comercial", "Business / Commercial"), note: t("no es PHI", "not PHI") },
    { ico: "⚠️", label: t("Salud / ACA / Medicare", "Health / ACA / Medicare"), note: t("ES PHI — necesita BAA", "IS PHI — needs a BAA") },
    { ico: "🟡", label: t("Vida", "Life"), note: t("cuidado si involucra salud", "caution if health is involved") },
  ];

  return (
    <div style={shell}>
      <button onClick={() => setOpen((o) => !o)} style={headBtn} aria-expanded={open}>
        <MessageSquare size={18} style={{ color: "#00d4ff", flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{t("¿Qué puedo textear? (y qué no)", "What can I text? (and what I can't)")}</span>
        <ChevronDown size={18} style={{ color: "#7aa3c0", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>

      {open && (
        <div style={body}>
          {/* Golden rule */}
          <div style={{ fontSize: 12.5, color: "var(--c-text,#cbd5e1)", lineHeight: 1.5, background: "var(--c-surface2,#0d2030)", border: "1px solid var(--c-border,#1c3a4f)", borderRadius: 8, padding: "10px 12px" }}>
            {t(
              "Regla simple: el problema no es hablar de seguros, es que el mensaje ligue a la persona con su SALUD o su plan médico. Eso es información médica protegida (PHI) y por SMS necesita el acuerdo HIPAA (BAA). Todo lo demás es libre.",
              "Simple rule: the issue isn't talking about insurance — it's a message tying the person to their HEALTH or health plan. That's Protected Health Information (PHI) and over SMS it needs the HIPAA agreement (BAA). Everything else is free.",
            )}
          </div>

          {/* Safe examples */}
          <div>
            <div style={{ ...heading, color: "#34d399" }}>{t("✅ Se puede SIEMPRE (sin BAA)", "✅ Always OK (no BAA)")}</div>
            {safe.map((s, i) => (
              <div key={i} style={{ ...exItem(), marginBottom: 5 }}>
                <CheckCircle2 size={14} style={{ color: "#34d399", flexShrink: 0, marginTop: 2 }} /> <span>{s}</span>
              </div>
            ))}
          </div>

          {/* PHI examples */}
          <div>
            <div style={{ ...heading, color: "#fca5a5" }}>{t("⚠️ NO sin BAA (esto es PHI)", "⚠️ NOT without a BAA (this is PHI)")}</div>
            {phi.map((s, i) => (
              <div key={i} style={{ ...exItem(), marginBottom: 5 }}>
                <AlertTriangle size={14} style={{ color: "#fca5a5", flexShrink: 0, marginTop: 2 }} /> <span>{s}</span>
              </div>
            ))}
          </div>

          {/* Per-line-of-business distinction */}
          <div>
            <div style={{ ...heading, color: "#7aa3c0" }}>{t("Depende del TIPO de seguro", "Depends on the TYPE of insurance")}</div>
            <div style={{ display: "grid", gap: 6 }}>
              {ramos.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--c-text2,#9fc1da)" }}>
                  <span style={{ flexShrink: 0 }}>{r.ico}</span>
                  <span style={{ fontWeight: 600, color: "var(--c-white,#eef6ff)", minWidth: 0 }}>{r.label}</span>
                  <span style={{ color: "var(--c-text3,#45688a)" }}>— {r.note}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Practical golden rule */}
          <div style={{ fontSize: 12.5, color: "#fde68a", lineHeight: 1.5, background: "#2a230f", border: "1px solid #854d0e", borderRadius: 8, padding: "10px 12px" }}>
            {t(
              "Lo más seguro: usa el SMS solo para INVITAR a hablar. Los detalles sensibles van por llamada, por el portal seguro o en persona. Ej.: «Tengo novedades de tu cobertura, ¿te llamo?» en vez de poner el detalle del plan de salud.",
              "Safest approach: use SMS only to INVITE a conversation. Sensitive detail goes by call, secure portal, or in person. E.g. “I have news about your coverage, can I call?” instead of spelling out the health-plan detail.",
            )}
          </div>

          {/* Legal disclaimer — this copy is a draft until counsel approves. */}
          <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", lineHeight: 1.45, fontStyle: "italic" }}>
            {t(
              "Orientación práctica, no asesoría legal. El texto final debe ser aprobado por nuestra abogada de HIPAA.",
              "Practical guidance, not legal advice. The final wording must be approved by our HIPAA attorney.",
            )}
          </div>
        </div>
      )}
    </div>
  );
}
